import { keepPreviousData, UseQueryOptions, useQuery } from '@tanstack/react-query'

import { authAPI } from '@/api/new-api'
import {
  AnnouncementRecipientModel,
  AnnouncementRecipientStatus,
  AnnouncementRecipientStatusEnum,
} from '@/models/features'
import { PersonDisplay } from '@/models/features/display-column.models'

export interface GetAnnouncementRecipientsRequest {
  id: number
  buildingId?: number
  skip?: number
  limit?: number
  query?: string
  status?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}

export type AnnouncementRecipientStatusCounts = Record<AnnouncementRecipientStatus, number>

export interface GetAnnouncementRecipientsResponse {
  /** All recipients matching the search query. Ignores the status filter (not the page total). */
  count: number
  /** Per-status totals: match the search query, ignore the status filter. */
  statusCounts: AnnouncementRecipientStatusCounts
  recipients: AnnouncementRecipientModel[]
}

interface AnnouncementRecipientRaw {
  // Note: the BE's per-column person details - only the fields enabled in its settings (EL-15064)
  residentDisplay?: PersonDisplay
  id: number
  firstName: string
  lastName: string
  email: string
  avatar: string
  clientId: number | null
  apartment: string | null
  phone?: string | null
  buildingNumber?: string | null
  street?: string | null
  isDelivered: 0 | 1
  isOpened: 0 | 1
  isClicked: 0 | 1
  group: {
    id: number
    value: string
  }
  updatedAt: number | null
}

export interface GetAnnouncementRecipientsResponseRaw {
  count: number
  countSent: number
  countDelivered: number
  countOpened: number
  countClicked: number
  recipients: AnnouncementRecipientRaw[]
}

interface StatusFlags {
  isDelivered: 0 | 1
  isOpened: 0 | 1
  isClicked: 0 | 1
}

const getStatusFromFlags = ({
  isDelivered,
  isOpened,
  isClicked,
}: StatusFlags): AnnouncementRecipientStatus => {
  if (isClicked === 1) return AnnouncementRecipientStatusEnum.enum.clicked
  if (isOpened === 1) return AnnouncementRecipientStatusEnum.enum.opened
  if (isDelivered === 1) return AnnouncementRecipientStatusEnum.enum.delivered
  return AnnouncementRecipientStatusEnum.enum.sent
}

export const transformRecipient = (raw: AnnouncementRecipientRaw): AnnouncementRecipientModel => {
  const { id, avatar, email, firstName, lastName, phone, buildingNumber, street } = raw

  return {
    avatar,
    buildingNumber,
    email,
    firstName,
    id,
    lastName,
    phone,
    residentId: raw.clientId || 0,
    residentDisplay: raw.residentDisplay,
    residentName: `${raw.firstName} ${raw.lastName}`.trim(),
    status: getStatusFromFlags({
      isClicked: raw.isClicked,
      isDelivered: raw.isDelivered,
      isOpened: raw.isOpened,
    }),
    street,
    unitNumber: raw.apartment || '-',
    updatedAt: raw.updatedAt ?? undefined,
  }
}

export const ANNOUNCEMENT_RECIPIENTS_BASE_QUERY_KEY = 'useGetAnnouncementRecipients'

export const getAnnouncementRecipientsQueryKey = (data: GetAnnouncementRecipientsRequest) => [
  ANNOUNCEMENT_RECIPIENTS_BASE_QUERY_KEY,
  data,
]

export const getAnnouncementRecipients = async (
  data: GetAnnouncementRecipientsRequest,
  signal?: AbortSignal,
): Promise<GetAnnouncementRecipientsResponse> => {
  const { id, buildingId, ...params } = data

  const response = await authAPI.get<GetAnnouncementRecipientsResponseRaw>(
    `/communication/building/${buildingId}/history/${id}/recipients`,
    { params, signal },
  )

  const { count, countSent, countDelivered, countOpened, countClicked, recipients } = response.data

  return {
    count,
    recipients: recipients.map(transformRecipient),
    statusCounts: {
      clicked: countClicked,
      delivered: countDelivered,
      opened: countOpened,
      sent: countSent,
    },
  }
}

export const useGetAnnouncementRecipients = (
  data: GetAnnouncementRecipientsRequest,
  options?: Omit<UseQueryOptions<GetAnnouncementRecipientsResponse, Error>, 'queryKey' | 'queryFn'>,
) => {
  const result = useQuery({
    enabled: !!data.id && !!data.buildingId,
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) => getAnnouncementRecipients({ ...data }, signal),
    queryKey: getAnnouncementRecipientsQueryKey(data),
    ...options,
  })

  return result
}
